import _ from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { clearApolloCache } from '@/shared/api';
import { EMITTER_CONSTANTS, KEY_RECOIL, TAB_KEY } from '@/shared/config';
import { useEmitter } from '@/shared/lib/customHooks';
import { fetchHolterTasks } from '@/entities/study';
import { checkLimitStudyProcessing, isHolterProcessingAvailable } from '@/shared/lib/aiUtils';
import { zeroPad, auth } from '@/shared/lib';
import { reportTabStatus } from '@/shared/lib/devLog';

import { fetchAIProcessingStatus, selectIsActiveTab, useReportStore } from '@/entities/report';
import emitter from '@/shared/lib/eventEmitter';
import { callStartHolterStudyProcessing, combineDailySummaries } from './helper';
import type { HrSummaryTabProps } from './index';

/**
 * Coordinates the HR Summary tab lifecycle — AI processing start, activation gating and daily summary merging. Marks the tab active on tab id 6, starts holter study processing once a socket id exists, gates activation on pending AI save requests, clears the queued request on the AI stop event, recombines original with edited daily summaries, and restarts processing after a socket reconnect.
 *
 * @param props - Carries activeTab, the id of the currently selected report tab.
 * @returns An object carrying the tab key, processing and loading flags, study and profile identifiers and the visited-tab ref.
 */
const useHrSummaryTab = ({ activeTab }: Pick<HrSummaryTabProps, 'activeTab'>) => {
  const keyRecoil = KEY_RECOIL.HR_SUMMARY;
  const isStartedAiProcessing = useReportStore((s) => s.isStartedAiProcessing);
  const setIsStartedAiProcessing = useReportStore((s) => s.actions.setIsStartedAiProcessing);
  const setIsOpenProcessingLimit = useReportStore((s) => s.actions.setIsOpenProcessingLimit);
  const setLimitProcessingStudy = useReportStore((s) => s.actions.setLimitProcessingStudy);
  const isShowTabHeader = useReportStore((s) => s.isShowTabHeader);
  // reportInfoState (Recoil) is typed {} | IReportInfo - pre-existing gap, needs a cast to destructure.
  const { studyId, profileId, isNotEdit, studyFid } = useReportStore((s) => s.reportInfo) as any;
  const isLoadedIndexDB = useReportStore((s) => s.isLoadedIndexDB);
  const isCompletedDownloadBeat = useReportStore((s) => s.isCompletedDownloadBeat);
  const isActiveTab = useReportStore(selectIsActiveTab(keyRecoil));
  const setIsActiveTabAction = useReportStore((s) => s.actions.setIsActiveTab);
  /**
   * Writes the active flag of the HR Summary tab into the report store. Forwards the value or updater to the store action along with this tab's key.
   *
   * @param v - Next active flag or an updater producing it.
   * @returns Nothing.
   */
  const setIsActiveTab = (v: Parameters<typeof setIsActiveTabAction>[1]) =>
    setIsActiveTabAction(keyRecoil, v);
  const [isTabActive, setIsTabActive] = useState(false);
  const hasVisitTab = useRef(false);
  const hasCallStart = useRef<any>();
  const originalDailySummaries = useReportStore((s) => s.originalDailySummaries);
  const setDailySummariesCombine = useReportStore((s) => s.actions.setDailySummariesCombine);
  const updatedDailySummaries = useReportStore((s) => s.updatedDailySummaries);

  /**
   * Kicks off AI holter processing for the current study and profile. Emits the pending-profiles update, calls the start mutation, refetches holter tasks, and on hitting the simultaneous-study limit opens the limit modal with the zero-padded blocking study ids; otherwise it records the start, opens the processing-limit modal when this study is unavailable, and marks AI processing started once the result is ready.
   *
   * @returns A promise resolving once processing has been requested and the limit modals settled.
   */
  const startHolterStudyProcessing = async () => {
    emitter.emit(EMITTER_CONSTANTS.UPDATE_PENDING_PROFILES, [{ id: profileId }]);
    const result = await callStartHolterStudyProcessing(studyId, profileId);
    const holterTasks = await fetchHolterTasks();
    emitter.emit(EMITTER_CONSTANTS.UPDATE_PENDING_PROFILES, holterTasks?.pendingProfiles || []);
    if (checkLimitStudyProcessing(result)) {
      setLimitProcessingStudy({
        isOpenModal: true,
        limitCount: result?.simultaneousStudyLimit,
        // zeroPad expects a number, but processingStudyFids (Apollo, StartHolterStudyProcessingResult) is string[] - pre-existing.
        studyFids: _.map(result?.processingStudyFids, (x) => zeroPad(x as any)),
      });
      return;
    }
    hasCallStart.current = true;
    if (!isHolterProcessingAvailable(holterTasks, studyFid)) {
      setIsOpenProcessingLimit(true);
    }
    if (result?.isReady) {
      setIsStartedAiProcessing(true);
    }
  };

  /**
   * Starts AI holter processing only once the socket connection exists. Reads the socket id from auth and reschedules itself every 500ms until one is available, then delegates to the routine that calls the start mutation and handles the study limit.
   *
   * @returns Nothing.
   */
  const startHolterStudyProcessingByRetry = () => {
    const socketId = auth.getSocketId();
    console.log('[HRSummary] startHolterStudyProcessingByRetry:socketId', { socketId });
    if (!socketId) {
      setTimeout(startHolterStudyProcessingByRetry, 500);
      return;
    }
    startHolterStudyProcessing();
  };

  useEffect(() => {
    const isActive = activeTab === TAB_KEY.HR_SUMMARY;
    setIsTabActive(false);
    setIsActiveTab(isActive);
    hasVisitTab.current = isActive;
  }, [activeTab]);

  useEffect(() => {
    reportTabStatus(keyRecoil, {
      isActiveTab,
      isStartedAiProcessing,
      hasCallStart: !!hasCallStart.current,
    });
    if (isActiveTab && !isStartedAiProcessing && !hasCallStart.current) {
      startHolterStudyProcessingByRetry();
    }
  }, [isActiveTab, isStartedAiProcessing]);

  /**
   * Decides whether the HR Summary tab may be entered while an AI save is still processing. Reads the queued request ids from localStorage, activates immediately when the queue is empty, belongs to this tab or processing already finished, otherwise polls each request's AI status, drops the completed ones from storage and activates only if at least one finished; a thrown error leaves the tab inactive.
   *
   * @returns A promise resolving once the tab active flag has been decided.
   */
  const checkCanSwitchTab = async () => {
    try {
      const keyStorage = `requestId-${profileId}-${studyFid}`;
      const storedResult = localStorage.getItem(keyStorage);
      const requestData = JSON.parse(storedResult as any);
      if (_.isEmpty(requestData)) return setIsTabActive(true);
      const { type, requestId } = requestData?.[0] || {};
      if (!requestId || type === keyRecoil) return setIsTabActive(true);
      const isProcessing = localStorage.getItem(`ai_process-${profileId}`);
      if (JSON.parse(isProcessing as any)) return setIsTabActive(true);
      const promisses: any[] = [];
      setIsTabActive(false);
      _.forEach(requestData, (x: any) => {
        if (x.requestId) {
          promisses.push(fetchAIProcessingStatus(x.requestId, 'switchTab'));
        }
      });
      const result = await Promise.all(promisses);
      let canSwitchTab = false;
      const updateSuccess: any[] = [];
      _.forEach(result, (x, i) => {
        if (x === true) {
          canSwitchTab = true;
          updateSuccess.push(requestData?.[i]);
        }
      });
      _.remove(
        requestData,
        (x: any) => !!_.find(updateSuccess, (y: any) => y.requestId === x.requestId),
      );
      localStorage.setItem(keyStorage, JSON.stringify(requestData));

      if (canSwitchTab) {
        localStorage.setItem(`ai_process-${profileId}`, JSON.stringify(canSwitchTab));
      }

      setIsTabActive(canSwitchTab);
    } catch (error) {
      console.log(error);
      setIsTabActive(false);
    }
    return null;
  };

  useEffect(() => {
    if (isActiveTab) {
      checkCanSwitchTab();
    }
  }, [isActiveTab]);

  useEmitter(
    EMITTER_CONSTANTS.AI_STOP_PROCESSING,
    async (msg: any) => {
      await clearApolloCache();
      if (isActiveTab && !_.isEmpty(msg)) {
        const { requestId } = msg || {};
        const keyStorage = `requestId-${profileId}-${studyFid}`;
        const storedResult = localStorage.getItem(keyStorage);
        const requestData = JSON.parse(storedResult as any);
        _.remove(requestData, (x: any) => x.requestId === requestId);
        localStorage.setItem(keyStorage, JSON.stringify(requestData));
        if (_.isEmpty(requestData)) {
          localStorage.setItem(`ai_process-${profileId}`, JSON.stringify(true));
          setIsTabActive(true);
        }
      }
    },
    [isActiveTab, profileId, studyFid],
  );

  useEffect(() => {
    if (originalDailySummaries?.length) {
      setDailySummariesCombine(
        combineDailySummaries(originalDailySummaries, updatedDailySummaries),
      );
    }
  }, [originalDailySummaries, updatedDailySummaries]);

  useEmitter(
    EMITTER_CONSTANTS.SOCKET_RECONNECTED,
    () => {
      if (isTabActive && !isStartedAiProcessing) {
        startHolterStudyProcessingByRetry();
      }
    },
    [isTabActive, isStartedAiProcessing],
  );

  return {
    keyRecoil,
    isStartedAiProcessing,
    isShowTabHeader,
    studyId,
    profileId,
    isNotEdit,
    isLoadedIndexDB,
    isCompletedDownloadBeat,
    isTabActive,
    hasVisitTab,
  };
};

export default useHrSummaryTab;
